import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  LayoutDashboard, Plus, Save, Trash2, GripVertical, Settings,
  Share2, Copy, Check, Lock,
  DollarSign, AlertTriangle, PiggyBank, PieChart, TrendingUp,
  Shield, Lightbulb, Cloud, Award, Users, Clock, AlertCircle,
  Box, Cpu, Zap, Star, Edit2, MoreVertical
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for widgets
const iconMap = {
  DollarSign, AlertTriangle, PiggyBank, PieChart, TrendingUp,
  Shield, Lightbulb, Cloud, Award, Users, Clock, AlertCircle,
  Box, Cpu, Zap, LayoutDashboard
};

const DashboardBuilder = () => {
  const { shouldShowData } = useDemoData();

  const [widgets, setWidgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [activeWidgets, setActiveWidgets] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [showNewLayoutDialog, setShowNewLayoutDialog] = useState(false);
  const [newLayoutName, setNewLayoutName] = useState('');
  const [newLayoutDesc, setNewLayoutDesc] = useState('');
  const [draggedWidget, setDraggedWidget] = useState(null);
  const [widgetData, setWidgetData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWidgets();
    fetchLayouts();
  }, []);

  useEffect(() => {
    if (selectedLayout) {
      setActiveWidgets(selectedLayout.widgets || []);
      // Fetch data for all active widgets
      selectedLayout.widgets?.forEach(w => {
        fetchWidgetData(w.id);
      });
    }
  }, [selectedLayout]);

  // Iter84ag — auth-gated dashboard-builder routes; always send the JWT.
  const _authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchWidgets = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard-builder/widgets`, {
        headers: _authHeaders(),
      });
      setWidgets(response.data.widgets);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to fetch widgets:', error);
    }
  };

  const fetchLayouts = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard-builder/layouts`, {
        headers: _authHeaders(),
      });
      setLayouts(response.data.layouts);
      // Select default layout
      const defaultLayout = response.data.layouts.find(l => l.isDefault) || response.data.layouts[0];
      if (defaultLayout) {
        setSelectedLayout(defaultLayout);
      }
    } catch (error) {
      console.error('Failed to fetch layouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWidgetData = async (widgetId) => {
    try {
      const response = await axios.get(`${API_URL}/dashboard-builder/widget-data/${widgetId}`, {
        headers: _authHeaders(),
      });
      setWidgetData(prev => ({
        ...prev,
        [widgetId]: response.data.data
      }));
    } catch (error) {
      console.error(`Failed to fetch data for widget ${widgetId}:`, error);
    }
  };

  const handleAddWidget = (widget) => {
    const newWidget = {
      id: widget.id,
      x: 0,
      y: Math.max(0, ...activeWidgets.map(w => w.y + w.h), 0),
      w: widget.size === 'small' ? 2 : widget.size === 'large' ? 8 : 4,
      h: widget.size === 'small' ? 2 : widget.size === 'large' ? 3 : 2,
      config: widget.defaultConfig || {}
    };
    setActiveWidgets([...activeWidgets, newWidget]);
    fetchWidgetData(widget.id);
    toast.success(`Added ${widget.name} to dashboard`);
  };

  const handleRemoveWidget = (index) => {
    const newWidgets = [...activeWidgets];
    newWidgets.splice(index, 1);
    setActiveWidgets(newWidgets);
    toast.success('Widget removed');
  };

  const handleSaveLayout = async () => {
    if (!selectedLayout) return;
    
    try {
      await axios.put(`${API_URL}/dashboard-builder/layouts/${selectedLayout.id}`, {
        name: selectedLayout.name,
        description: selectedLayout.description,
        widgets: activeWidgets,
        isDefault: selectedLayout.isDefault
      }, { headers: _authHeaders() });
      toast.success('Dashboard layout saved');
      setIsEditing(false);
      fetchLayouts();
    } catch (error) {
      toast.error('Failed to save layout');
    }
  };

  const handleCreateLayout = async () => {
    if (!newLayoutName.trim()) {
      toast.error('Please enter a layout name');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/dashboard-builder/layouts`, {
        name: newLayoutName,
        description: newLayoutDesc,
        widgets: [],
        isDefault: false
      }, { headers: _authHeaders() });
      setLayouts([...layouts, response.data]);
      setSelectedLayout(response.data);
      setActiveWidgets([]);
      setNewLayoutName('');
      setNewLayoutDesc('');
      setShowNewLayoutDialog(false);
      setIsEditing(true);
      toast.success('New dashboard created');
    } catch (error) {
      toast.error('Failed to create layout');
    }
  };

  const handleSetDefault = async (layoutId) => {
    try {
      await axios.post(`${API_URL}/dashboard-builder/layouts/${layoutId}/set-default`, {}, {
        headers: _authHeaders(),
      });
      toast.success('Default dashboard updated');
      fetchLayouts();
    } catch (error) {
      toast.error('Failed to set default');
    }
  };

  const handleDeleteLayout = async (layoutId) => {
    if (layouts.length <= 1) {
      toast.error('Cannot delete the only layout');
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/dashboard-builder/layouts/${layoutId}`, {
        headers: _authHeaders(),
      });
      toast.success('Dashboard deleted');
      fetchLayouts();
    } catch (error) {
      toast.error('Failed to delete layout');
    }
  };

  // ── Share dashboard via signed link ────────────────────────────────────────
  const [shareDialog, setShareDialog] = useState(null);  // { layoutId, layoutName, link, hasPassword }
  const [shareExpiry, setShareExpiry] = useState(72);  // hours
  const [sharePassword, setSharePassword] = useState('');
  const [shareLabel, setShareLabel] = useState('');
  const [creatingShare, setCreatingShare] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const handleShareDashboard = (layout) => {
    setShareDialog({ layoutId: layout.id, layoutName: layout.name, link: null });
    setShareExpiry(72);
    setSharePassword('');
    setShareLabel(`Shared: ${layout.name}`);
    setShareCopied(false);
  };

  const submitShareDashboard = async () => {
    if (!shareDialog) return;
    setCreatingShare(true);
    try {
      const r = await axios.post(`${API_URL}/share`, {
        artifact_type: 'dashboard',
        artifact_id: shareDialog.layoutId,
        expires_in_hours: shareExpiry,
        password: sharePassword.trim() || undefined,
        label: shareLabel.trim() || undefined,
      });
      setShareDialog({ ...shareDialog, link: r.data.share_url, hasPassword: !!sharePassword.trim() });
      toast.success('Share link created!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create share link');
    } finally {
      setCreatingShare(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareDialog?.link) return;
    try {
      await navigator.clipboard.writeText(shareDialog.link);
      setShareCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setShareCopied(false), 2500);
    } catch {
      toast.error('Could not copy');
    }
  };

  const getWidgetInfo = (widgetId) => {
    return widgets.find(w => w.id === widgetId) || {};
  };

  const renderWidgetContent = (widgetId, data) => {
    const widget = getWidgetInfo(widgetId);
    const IconComponent = iconMap[widget.icon] || LayoutDashboard;
    
    if (!data) {
      return (
        <div className="flex items-center justify-center h-full text-slate-400">
          <div className="animate-pulse">Loading...</div>
        </div>
      );
    }

    switch (widgetId) {
      case 'cost-summary':
        return (
          <div className="space-y-2">
            <div className="text-3xl font-bold text-slate-900 dark:text-white">
              ${data.totalMtd?.toLocaleString()}
            </div>
            <div className={`text-sm flex items-center gap-1 ${data.change >= 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
              <TrendingUp className={`w-4 h-4 ${data.change < 0 ? 'rotate-180' : ''}`} />
              {data.change >= 0 ? '+' : ''}{data.change}% vs last month
            </div>
          </div>
        );

      case 'anomaly-count':
        return (
          <div className="flex items-center gap-4">
            <div className="text-4xl font-bold text-amber-500">{data.count}</div>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-rose-500" />
                <span>{data.critical} Critical</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <span>{data.high} High</span>
              </div>
            </div>
          </div>
        );

      case 'savings-potential':
        return (
          <div className="space-y-2">
            <div className="text-3xl font-bold text-emerald-500">
              ${data.total?.toLocaleString()}
            </div>
            <div className="text-sm text-slate-400">
              {data.recommendations} recommendations
            </div>
          </div>
        );

      case 'health-score':
        return (
          <div className="flex items-center gap-4">
            <div className={`text-4xl font-bold ${
              data.grade === 'A' ? 'text-emerald-500' : 
              data.grade === 'B' ? 'text-emerald-500' : 
              data.grade === 'C' ? 'text-amber-500' : 'text-rose-500'
            }`}>
              {data.score}
            </div>
            <div className="space-y-1">
              <Badge className={`${
                data.grade === 'A' ? 'bg-emerald-100 text-emerald-700' : 
                data.grade === 'B' ? 'bg-emerald-100 text-emerald-700' : 
                'bg-amber-100 text-amber-700'
              }`}>
                Grade {data.grade}
              </Badge>
              <div className="text-xs text-slate-400">
                {data.trend === 'up' ? 'Improving' : 'Needs attention'}
              </div>
            </div>
          </div>
        );

      case 'cloud-provider-split':
        return (
          <div className="space-y-3">
            {data.providers?.map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-16 text-sm font-medium">{p.name}</div>
                <div className="flex-1 h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full"
                    style={{ width: `${p.percent}%`, backgroundColor: p.color }}
                  />
                </div>
                <div className="w-20 text-right text-sm">${(p.cost/1000).toFixed(1)}k</div>
              </div>
            ))}
          </div>
        );

      case 'budget-status':
        return (
          <div className="space-y-3">
            {data.budgets?.slice(0, 3).map((b, i) => (
              <div key={i} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{b.name}</span>
                  <span className={b.percent > 80 ? 'text-amber-500' : 'text-slate-400'}>
                    {b.percent.toFixed(0)}%
                  </span>
                </div>
                <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${
                      b.percent > 90 ? 'bg-rose-500' : 
                      b.percent > 80 ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${Math.min(b.percent, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        );

      case 'forecast-widget':
        return (
          <div className="space-y-2">
            <div className="text-2xl font-bold text-slate-900 dark:text-white">
              ${data.predicted?.toLocaleString()}
            </div>
            <div className="text-sm text-slate-400">
              Range: ${data.lowerBound?.toLocaleString()} - ${data.upperBound?.toLocaleString()}
            </div>
            <Badge className="bg-emerald-100 text-emerald-700">
              {(data.confidence * 100).toFixed(0)}% confidence
            </Badge>
          </div>
        );

      default:
        return (
          <div className="flex items-center gap-3">
            <IconComponent className="w-8 h-8 text-emerald-500" />
            <div>
              <div className="font-medium">{widget.name}</div>
              <div className="text-sm text-slate-400">{widget.description}</div>
            </div>
          </div>
        );
    }
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Dashboard Builder" description="Connect your cloud account to see real data here." />;
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-builder-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-3">
            <LayoutDashboard className="w-7 h-7 text-emerald-500" />
            Dashboard Builder
          </h1>
          <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">
            Create custom dashboards with drag-and-drop widgets
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  if (selectedLayout) {
                    setActiveWidgets(selectedLayout.widgets || []);
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveLayout}
                className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                data-testid="save-layout-btn"
              >
                <Save className="w-4 h-4" />
                Save Layout
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setIsEditing(true)}
                className="gap-2"
                data-testid="edit-layout-btn"
              >
                <Edit2 className="w-4 h-4" />
                Edit Dashboard
              </Button>
              <Dialog open={showNewLayoutDialog} onOpenChange={setShowNewLayoutDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="new-layout-btn">
                    <Plus className="w-4 h-4" />
                    New Dashboard
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Dashboard</DialogTitle>
                    <DialogDescription>
                      Give your custom dashboard a name and description
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Dashboard Name</label>
                      <Input
                        placeholder="e.g., My Custom Dashboard"
                        value={newLayoutName}
                        onChange={(e) => setNewLayoutName(e.target.value)}
                        data-testid="layout-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Description (optional)</label>
                      <Input
                        placeholder="e.g., Daily monitoring view"
                        value={newLayoutDesc}
                        onChange={(e) => setNewLayoutDesc(e.target.value)}
                        data-testid="layout-desc-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowNewLayoutDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateLayout} className="bg-emerald-600 hover:bg-emerald-700">
                      Create Dashboard
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Layout Selector */}
      <div className="flex flex-wrap gap-2">
        {layouts.map((layout) => (
          <Button
            key={layout.id}
            variant={selectedLayout?.id === layout.id ? "default" : "outline"}
            className={`gap-2 ${selectedLayout?.id === layout.id ? 'bg-emerald-600' : ''}`}
            onClick={() => {
              setSelectedLayout(layout);
              setIsEditing(false);
            }}
            data-testid={`layout-${layout.id}`}
          >
            {layout.isDefault && <Star className="w-3 h-3 fill-current" />}
            {layout.name}
          </Button>
        ))}
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-4 gap-6">
        {/* Widget Palette (shown when editing) */}
        {isEditing && (
          <Card className="lg:col-span-1 h-fit sticky top-20">
            <CardHeader>
              <CardTitle className="text-base">Available Widgets</CardTitle>
              <CardDescription>Click to add to dashboard</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 max-h-[60vh] overflow-y-auto">
              {categories.map((category) => (
                <div key={category.id} className="space-y-2">
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    {category.name}
                  </h4>
                  <div className="space-y-1">
                    {widgets
                      .filter(w => w.category === category.id)
                      .map((widget) => {
                        const IconComponent = iconMap[widget.icon] || LayoutDashboard;
                        const isAdded = activeWidgets.some(aw => aw.id === widget.id);
                        return (
                          <button
                            key={widget.id}
                            onClick={() => !isAdded && handleAddWidget(widget)}
                            disabled={isAdded}
                            className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-all ${
                              isAdded 
                                ? 'bg-slate-100 dark:bg-slate-800 opacity-50 cursor-not-allowed' 
                                : 'hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-500/30 border border-transparent'
                            }`}
                            data-testid={`widget-${widget.id}`}
                          >
                            <IconComponent className="w-4 h-4 text-slate-400" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{widget.name}</p>
                              <p className="text-xs text-slate-400 truncate">{widget.description}</p>
                            </div>
                            {isAdded && <Check className="w-4 h-4 text-emerald-500" />}
                          </button>
                        );
                      })}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Dashboard Preview */}
        <div className={`${isEditing ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
          {activeWidgets.length === 0 ? (
            <Card className="p-12 text-center">
              <LayoutDashboard className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                {isEditing ? 'Start Building Your Dashboard' : 'Empty Dashboard'}
              </h3>
              <p className="text-slate-400 mb-4">
                {isEditing 
                  ? 'Click widgets from the palette to add them to your dashboard'
                  : 'Click "Edit Dashboard" to start customizing'
                }
              </p>
              {!isEditing && (
                <Button onClick={() => setIsEditing(true)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Edit2 className="w-4 h-4 mr-2" />
                  Start Editing
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-8 gap-4 auto-rows-[100px]">
              {activeWidgets.map((activeWidget, index) => {
                const widget = getWidgetInfo(activeWidget.id);
                const IconComponent = iconMap[widget.icon] || LayoutDashboard;
                const data = widgetData[activeWidget.id];
                
                return (
                  <Card 
                    key={`${activeWidget.id}-${index}`}
                    className={`relative group hover:shadow-lg transition-shadow ${
                      activeWidget.w >= 8 ? 'col-span-8' :
                      activeWidget.w >= 4 ? 'col-span-8 sm:col-span-4' :
                      'col-span-8 sm:col-span-4 lg:col-span-2'
                    }`}
                    style={{ gridRow: `span ${activeWidget.h}` }}
                    data-testid={`active-widget-${activeWidget.id}`}
                  >
                    {isEditing && (
                      <div className="absolute -top-2 -right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          size="icon"
                          variant="destructive"
                          className="h-6 w-6 rounded-full"
                          onClick={() => handleRemoveWidget(index)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <IconComponent className="w-4 h-4 text-emerald-500" />
                        {widget.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {renderWidgetContent(activeWidget.id, data)}
                    </CardContent>
                    {isEditing && (
                      <div className="absolute bottom-2 right-2 cursor-move opacity-0 group-hover:opacity-100 transition-opacity">
                        <GripVertical className="w-4 h-4 text-slate-400" />
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Layout Info */}
      {selectedLayout && !isEditing && (
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  {selectedLayout.name}
                  {selectedLayout.isDefault && (
                    <Badge className="bg-emerald-100 text-emerald-700">Default</Badge>
                  )}
                </h3>
                <p className="text-sm text-slate-400">{selectedLayout.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!selectedLayout.isDefault && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSetDefault(selectedLayout.id)}
                  className="gap-1"
                >
                  <Star className="w-3 h-3" />
                  Set as Default
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleShareDashboard(selectedLayout)}
                className="gap-1"
                data-testid="share-dashboard-btn"
              >
                <Share2 className="w-3 h-3" />
                Share
              </Button>
              {layouts.length > 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteLayout(selectedLayout.id)}
                  className="gap-1 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ── Share Dashboard Dialog ──────────────────────────────────────── */}
      <Dialog open={!!shareDialog} onOpenChange={(o) => !o && setShareDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="share-dashboard-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-emerald-500" />
              {shareDialog?.link ? 'Share link ready' : 'Create share link'}
            </DialogTitle>
            <DialogDescription>
              {shareDialog?.link
                ? 'Anyone with this link can view this dashboard read-only. Revoke any time from Organization → Share Links.'
                : `Create a signed, scoped, expiring link for "${shareDialog?.layoutName}".`}
            </DialogDescription>
          </DialogHeader>

          {!shareDialog?.link ? (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="share-label">Label <span className="text-slate-400">(optional)</span></Label>
                <Input
                  id="share-label"
                  value={shareLabel}
                  onChange={(e) => setShareLabel(e.target.value)}
                  placeholder="e.g. Q2 Board Review"
                  maxLength={100}
                  data-testid="share-label-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="share-expiry">Expires in</Label>
                <select
                  id="share-expiry"
                  value={shareExpiry}
                  onChange={(e) => setShareExpiry(parseInt(e.target.value))}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                  data-testid="share-expiry-select"
                >
                  <option value={1}>1 hour</option>
                  <option value={24}>24 hours (1 day)</option>
                  <option value={72}>72 hours (3 days)</option>
                  <option value={168}>168 hours (1 week)</option>
                  <option value={720}>720 hours (30 days)</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="share-password" className="flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" />
                  Password <span className="text-slate-400 font-normal">(optional)</span>
                </Label>
                <Input
                  id="share-password"
                  type="password"
                  value={sharePassword}
                  onChange={(e) => setSharePassword(e.target.value)}
                  placeholder="Adds an extra unlock step for the recipient"
                  data-testid="share-password-input"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="text-xs font-bold tracking-widest text-emerald-700 dark:text-emerald-300 mb-2">SHARE URL</div>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={shareDialog.link}
                    className="text-xs font-mono"
                    onClick={(e) => e.target.select()}
                    data-testid="share-url-input"
                  />
                  <Button onClick={copyShareLink} size="sm" data-testid="share-copy-btn">
                    {shareCopied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              {shareDialog.hasPassword && (
                <div className="text-xs text-slate-400 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-amber-500" />
                  Password-protected — recipient must enter the password to view.
                </div>
              )}
              <p className="text-xs text-slate-400">
                Track views and revoke this link from <strong>Organization → Share Links</strong>.
              </p>
            </div>
          )}

          <DialogFooter>
            {!shareDialog?.link ? (
              <>
                <Button variant="outline" onClick={() => setShareDialog(null)} disabled={creatingShare}>Cancel</Button>
                <Button onClick={submitShareDashboard} disabled={creatingShare} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950" data-testid="share-create-btn">
                  {creatingShare ? 'Creating…' : <><Share2 className="w-4 h-4 mr-1.5" />Create link</>}
                </Button>
              </>
            ) : (
              <Button onClick={() => setShareDialog(null)} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950" data-testid="share-done-btn">
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardBuilder;
